/**
 * AddSyringeForm Component
 *
 * Dialog form for adding a third-party culture syringe purchased from external supplier.
 * Supports inline supplier creation and quick-add strain creation for new strains.
 */

import { useState, useEffect, useMemo } from 'react'
import { toLocalDateString } from '@/utils/dateUtils'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Card, CardContent } from '@/components/ui/card'
import { useStrains } from '@/hooks/useLC'
import { useSuppliers } from '@/hooks/useSuppliers'
import { useCultureSyringes } from '@/hooks/useCultureSyringes'
import SupplierDialog from '@/components/inventory/SupplierDialog'
import { QuickAddStrainModal } from '@/components/production/strains'
import { Syringe, Loader2, Plus, Calendar, AlertCircle } from 'lucide-react'

// Common syringe volumes
const VOLUME_OPTIONS = [
  { value: '10', label: '10ml' },
  { value: '12', label: '12ml' },
  { value: '20', label: '20ml' },
  { value: '30', label: '30ml' },
  { value: 'custom', label: 'Custom...' },
]

export default function AddSyringeForm({
  open,
  onOpenChange,
  onSuccess
}) {
  // Form state
  const [supplierId, setSupplierId] = useState('')
  const [supplierBatchCode, setSupplierBatchCode] = useState('')
  const [strainId, setStrainId] = useState('')
  const [volumeOption, setVolumeOption] = useState('')
  const [customVolume, setCustomVolume] = useState('')
  const [dateReceived, setDateReceived] = useState('')
  const [storageLocation, setStorageLocation] = useState('')
  const [notes, setNotes] = useState('')
  const [isSaving, setIsSaving] = useState(false)
  const [error, setError] = useState(null)

  // Dialog states
  const [showSupplierDialog, setShowSupplierDialog] = useState(false)
  const [showStrainDialog, setShowStrainDialog] = useState(false)
  const [existingSupplierIds, setExistingSupplierIds] = useState(new Set())
  const [existingStrainIds, setExistingStrainIds] = useState(new Set())

  // Hooks
  const { strains, refetch: refetchStrains } = useStrains()
  const { suppliers, refetch: refetchSuppliers } = useSuppliers()
  const { createSyringe } = useCultureSyringes()

  // Calculate volume
  const volume = useMemo(() => {
    if (volumeOption === 'custom') {
      return parseFloat(customVolume) || 0
    }
    return parseFloat(volumeOption) || 0
  }, [volumeOption, customVolume])

  // Calculate expiry date (42 days from received date)
  const expiryDate = useMemo(() => {
    if (!dateReceived) return null
    const date = new Date(dateReceived)
    date.setDate(date.getDate() + 42)
    return toLocalDateString(date)
  }, [dateReceived])

  // Days until expiry
  const daysUntilExpiry = useMemo(() => {
    if (!expiryDate) return null
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    const expiry = new Date(expiryDate)
    const diffTime = expiry - today
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24))
  }, [expiryDate])

  // Reset form when modal opens
  useEffect(() => {
    if (open) {
      setSupplierId('')
      setSupplierBatchCode('')
      setStrainId('')
      setVolumeOption('')
      setCustomVolume('')
      setDateReceived(toLocalDateString(new Date()))
      setStorageLocation('')
      setNotes('')
      setError(null)
    }
  }, [open])

  // Handle save
  const handleSave = async () => {
    // Validation
    if (!supplierId) {
      setError('Supplier is required')
      return
    }
    if (!supplierBatchCode.trim()) {
      setError("Supplier's batch code is required")
      return
    }
    if (!strainId) {
      setError('Strain is required')
      return
    }
    if (!volume || volume <= 0) {
      setError('Volume must be greater than 0')
      return
    }
    if (!dateReceived) {
      setError('Date received is required')
      return
    }

    setIsSaving(true)
    setError(null)

    try {
      const syringeData = {
        supplier_batch_code: supplierBatchCode.trim(),
        strain_id: strainId,
        supplier_id: supplierId,
        volume_ml: volume,
        received_date: dateReceived,
        storage_location: storageLocation.trim() || null,
        notes: notes.trim() || null,
      }

      const savedSyringe = await createSyringe(syringeData)
      onSuccess?.(savedSyringe)
      onOpenChange(false)
    } catch (err) {
      console.error('[AddSyringeForm] Save error:', err)
      setError(err.message || 'Failed to save syringe')
    } finally {
      setIsSaving(false)
    }
  }

  // Handle cancel
  const handleCancel = () => {
    onOpenChange(false)
  }

  // Handle supplier created - auto-select the new supplier
  const handleSupplierCreated = async () => {
    await refetchSuppliers()
    setShowSupplierDialog(false)
  }

  // Handle strain created - auto-select the new strain
  const handleStrainCreated = async (newStrain) => {
    await refetchStrains()
    if (newStrain?.id) {
      setStrainId(newStrain.id)
    }
    setShowStrainDialog(false)
  }

  // Effect to auto-select new supplier after refetch
  useEffect(() => {
    if (existingSupplierIds.size > 0 && suppliers.length > existingSupplierIds.size) {
      const newSupplier = suppliers.find(s => !existingSupplierIds.has(s.id))
      if (newSupplier) {
        setSupplierId(newSupplier.id)
        setExistingSupplierIds(new Set())
      }
    }
  }, [suppliers, existingSupplierIds])

  // Effect to auto-select new strain after refetch
  useEffect(() => {
    if (existingStrainIds.size > 0 && strains.length > existingStrainIds.size) {
      const newStrain = strains.find(s => !existingStrainIds.has(s.id))
      if (newStrain) {
        setStrainId(newStrain.id)
        setExistingStrainIds(new Set())
      }
    }
  }, [strains, existingStrainIds])

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md max-h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Syringe className="h-5 w-5" />
            Add Culture Syringe
          </DialogTitle>
          <DialogDescription>
            Add a liquid culture syringe purchased from an external supplier.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto min-h-0 space-y-4 -mx-6 px-6 py-2">
          {/* Supplier */}
          <div className="space-y-2">
            <Label htmlFor="supplier">
              Supplier <span className="text-destructive">*</span>
            </Label>
            <Select
              value={supplierId}
              onValueChange={(value) => {
                if (value === '__create_new__') {
                  setExistingSupplierIds(new Set(suppliers.map(s => s.id)))
                  setShowSupplierDialog(true)
                } else {
                  setSupplierId(value)
                }
              }}
              disabled={isSaving}
            >
              <SelectTrigger id="supplier">
                <SelectValue placeholder="Select supplier..." />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__create_new__">
                  <span className="flex items-center gap-2 text-primary">
                    <Plus className="h-4 w-4" />
                    Create new supplier
                  </span>
                </SelectItem>
                {suppliers.map(supplier => (
                  <SelectItem key={supplier.id} value={supplier.id}>
                    {supplier.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Supplier Batch Code */}
          <div className="space-y-2">
            <Label htmlFor="batch-code">
              Supplier&apos;s Batch Code <span className="text-destructive">*</span>
            </Label>
            <Input
              id="batch-code"
              value={supplierBatchCode}
              onChange={(e) => setSupplierBatchCode(e.target.value)}
              placeholder="e.g., MYC-2024-0892"
              disabled={isSaving}
            />
            <p className="text-xs text-muted-foreground">
              The batch/lot code from the supplier&apos;s label
            </p>
          </div>

          {/* Strain */}
          <div className="space-y-2">
            <Label htmlFor="strain">
              Strain <span className="text-destructive">*</span>
            </Label>
            <Select
              value={strainId}
              onValueChange={(value) => {
                if (value === '__create_new__') {
                  setExistingStrainIds(new Set(strains.map(s => s.id)))
                  setShowStrainDialog(true)
                } else {
                  setStrainId(value)
                }
              }}
              disabled={isSaving}
            >
              <SelectTrigger id="strain">
                <SelectValue placeholder="Select strain..." />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__create_new__">
                  <span className="flex items-center gap-2 text-primary">
                    <Plus className="h-4 w-4" />
                    Add new strain
                  </span>
                </SelectItem>
                {strains.map(strain => (
                  <SelectItem key={strain.id} value={strain.id}>
                    {strain.name} ({strain.code})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Volume */}
          <div className="space-y-2">
            <Label htmlFor="volume">
              Volume <span className="text-destructive">*</span>
            </Label>
            <div className="grid grid-cols-2 gap-2">
              <Select
                value={volumeOption}
                onValueChange={setVolumeOption}
                disabled={isSaving}
              >
                <SelectTrigger id="volume">
                  <SelectValue placeholder="Select volume..." />
                </SelectTrigger>
                <SelectContent>
                  {VOLUME_OPTIONS.map(opt => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {volumeOption === 'custom' && (
                <div className="relative">
                  <Input
                    type="number"
                    value={customVolume}
                    onChange={(e) => setCustomVolume(e.target.value)}
                    placeholder="0.0"
                    min="0"
                    step="0.5"
                    disabled={isSaving}
                    className="pr-8"
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">
                    ml
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Date Received and Storage Row */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="date-received">
                Date Received <span className="text-destructive">*</span>
              </Label>
              <Input
                id="date-received"
                type="date"
                value={dateReceived}
                onChange={(e) => setDateReceived(e.target.value)}
                disabled={isSaving}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="storage">Storage Location</Label>
              <Input
                id="storage"
                value={storageLocation}
                onChange={(e) => setStorageLocation(e.target.value)}
                placeholder="e.g., Fridge A"
                disabled={isSaving}
              />
            </div>
          </div>

          {/* Expiry Preview Card */}
          {expiryDate && (
            <Card className="bg-muted/50">
              <CardContent className="py-3 px-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 text-sm">
                    <Calendar className="h-4 w-4 text-muted-foreground" />
                    <span className="text-muted-foreground">Expires:</span>
                    <span className="font-medium">
                      {new Date(expiryDate).toLocaleDateString('en-GB', {
                        day: 'numeric',
                        month: 'short',
                        year: 'numeric',
                      })}
                    </span>
                  </div>
                  <span className={`text-sm font-medium ${
                    daysUntilExpiry <= 14 ? 'text-amber-600' : 'text-green-600'
                  }`}>
                    {daysUntilExpiry} days
                  </span>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Notes */}
          <div className="space-y-2">
            <Label htmlFor="notes">Notes</Label>
            <Textarea
              id="notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Any additional notes about this syringe..."
              rows={2}
              disabled={isSaving}
            />
          </div>

          {/* Error Message */}
          {error && (
            <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-md flex items-start gap-2">
              <AlertCircle className="h-4 w-4 text-destructive mt-0.5" />
              <p className="text-sm text-destructive">{error}</p>
            </div>
          )}
        </div>

        <DialogFooter className="border-t pt-4 mt-2">
          <Button variant="outline" onClick={handleCancel} disabled={isSaving}>
            Cancel
          </Button>
          <Button variant="create" onClick={handleSave} disabled={isSaving}>
            {isSaving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Add Syringe
          </Button>
        </DialogFooter>
      </DialogContent>

      {/* Supplier Creation Dialog */}
      <SupplierDialog
        open={showSupplierDialog}
        onOpenChange={setShowSupplierDialog}
        supplier={null}
        onSuccess={handleSupplierCreated}
      />

      {/* Strain Creation Dialog */}
      <QuickAddStrainModal
        open={showStrainDialog}
        onOpenChange={setShowStrainDialog}
        onSuccess={handleStrainCreated}
      />
    </Dialog>
  )
}
